import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from '../shell/AppShell';
import { AddContract } from '../surfaces/AddContract';
import { AnalyticsDashboard } from '../surfaces/AnalyticsDashboard';
import { AnalyticsOverview } from '../surfaces/AnalyticsOverview';
import { BaseDeviationReport } from '../surfaces/BaseDeviationReport';
import { BaseMsa } from '../surfaces/BaseMsa';
import { ClauseDetail } from '../surfaces/ClauseDetail';
import { ClauseLibrary } from '../surfaces/ClauseLibrary';
import { ContractRecord } from '../surfaces/ContractRecord';
import { CorpusReconciliation } from '../surfaces/CorpusReconciliation';
import { ExtractionFields } from '../surfaces/ExtractionFields';
import { TypeProposals } from '../surfaces/TypeProposals';
import { FieldAccuracy } from '../surfaces/FieldAccuracy';
import { IngestPlanner } from '../surfaces/IngestPlanner';
import { MatchReview } from '../surfaces/MatchReview';
import { NeedsAttention } from '../surfaces/NeedsAttention';
import { NotFound } from '../surfaces/NotFound';
import { Notifications } from '../surfaces/Notifications';
import { PartyAliases } from '../surfaces/PartyAliases';
import { PipelineStatus } from '../surfaces/PipelineStatus';
import { Search } from '../surfaces/Search';
import { SearchQuality } from '../surfaces/SearchQuality';
import { VerificationPolicy } from '../surfaces/VerificationPolicy';
import { VendorImport } from '../surfaces/VendorImport';
import { VerificationQueue } from '../surfaces/VerificationQueue';
import {
  ADMIN_NAV_GROUP,
  ANALYTICS_NAV_GROUP,
  BASE_DEVIATION_REPORT_PATH,
  CLAUSE_DETAIL_PATH,
  CONTRACT_RECORD_PATH,
  DEFAULT_PATH,
  NAV_GROUPS,
} from './surfaces';

/**
 * Ten surfaces in three groups, plus the admin bulk-ingest planner (CLM-43),
 * all nested inside AppShell so the rail and top bar persist across navigation.
 *
 * Paths are read from NAV_GROUPS rather than written out again, so a surface
 * cannot drift between the rail and the router. Only the two paths that are not
 * in the nav — the keyed contract record and the catch-all — are literal.
 *
 * There is deliberately no route guard. Every surface is reachable by every
 * signed-in user, because authorisation is the API's job (NFR-06) and a router
 * that redirects on a claim is exactly the interface-layer authorisation the
 * requirement forbids. Claims may dim a nav item; they never block a route.
 */

const SURFACE_ELEMENTS: Readonly<Record<string, JSX.Element>> = {
  dash: <NeedsAttention />,
  search: <Search />,
  detail: <ContractRecord />,
  upload: <AddContract />,
  clauses: <ClauseLibrary />,
  review: <MatchReview />,
  verify: <VerificationQueue />,
  alias: <PartyAliases />,
  quality: <SearchQuality />,
  notif: <Notifications />,
};

/** The admin group's surfaces (CLM-43 planner, CLM-35 reconciliation, CLM-70 pipeline, CLM-107 accuracy, CLM-111 base MSA, …, CLM-108 auto-approval, CLM-113 vendor list), keyed like SURFACE_ELEMENTS. */
const ADMIN_SURFACE_ELEMENTS: Readonly<Record<string, JSX.Element>> = {
  ingest: <IngestPlanner />,
  reconcile: <CorpusReconciliation />,
  pipeline: <PipelineStatus />,
  'field-accuracy': <FieldAccuracy />,
  'base-msa': <BaseMsa />,
  'extraction-fields': <ExtractionFields />,
  'type-proposals': <TypeProposals />,
  'verification-policy': <VerificationPolicy />,
  'vendor-import': <VendorImport />,
};

/** CLM-81 — the two Analytics boards, keyed like SURFACE_ELEMENTS. */
const ANALYTICS_SURFACE_ELEMENTS: Readonly<Record<string, JSX.Element>> = {
  'analytics-overview': <AnalyticsOverview />,
  'analytics-dashboard': <AnalyticsDashboard />,
};

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Navigate to={DEFAULT_PATH} replace />} />

        {/* CLM-96 (R-96-1) — the Overview boards, first, mirroring NavRail's
            order. No guard, like every other route: the API scopes the
            figures (NFR-06). */}
        {ANALYTICS_NAV_GROUP.surfaces.map((surface) => (
          <Route key={surface.id} path={surface.path} element={ANALYTICS_SURFACE_ELEMENTS[surface.id]} />
        ))}

        {NAV_GROUPS.flatMap((group) =>
          group.surfaces.map((surface) => (
            <Route key={surface.id} path={surface.path} element={SURFACE_ELEMENTS[surface.id]} />
          )),
        )}

        {/* CLM-43 — the admin bulk-ingest planner. Registered unconditionally,
            like every other route: the rail hides the entry from clear
            non-admins (an honest mirror of the API's 404-to-non-admins), but a
            deep link still resolves and the surface explains itself. The API is
            the authorisation boundary (NFR-06). */}
        {ADMIN_NAV_GROUP.surfaces.map((surface) => (
          <Route key={surface.id} path={surface.path} element={ADMIN_SURFACE_ELEMENTS[surface.id]} />
        ))}

        {/* Keyed record. Same component; it reads the param itself. */}
        <Route path={CONTRACT_RECORD_PATH} element={<ContractRecord />} />

        {/* One base-MSA deviation report under its subject's record (CLM-112). Reads both params itself; the API scopes it on both contracts. */}
        <Route path={BASE_DEVIATION_REPORT_PATH} element={<BaseDeviationReport />} />

        {/* One clause of the library, by code (CLM-55). Reads the param itself. */}
        <Route path={CLAUSE_DETAIL_PATH} element={<ClauseDetail />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
